/**
 * 增强的任务复杂度分析系统
 * 
 * 基于多维度评估任务复杂度，提供更准确的时间估算和任务拆分建议
 */

import { logger } from '../utils/logger';
import { Task, TaskPriority } from '../types';

export interface ComplexityFactors {
  // 基础因素
  textLength: number;          // 文本长度 (0-10)
  technicalTerms: number;      // 技术术语密度 (0-10)
  dependencies: number;        // 依赖复杂度 (0-10)
  uncertainty: number;         // 不确定性 (0-10)
  
  // 技术因素
  integration: number;         // 集成复杂度 (0-10)
  algorithmicDepth: number;    // 算法深度 (0-10)
  dataComplexity: number;      // 数据复杂度 (0-10)
  
  // 范围因素
  scope: number;              // 影响范围 (0-10)
  crossFunctional: number;    // 跨功能程度 (0-10)
  userImpact: number;         // 用户影响 (0-10)
  
  // 风险因素
  securityRisk: number;       // 安全风险 (0-10)
  performanceRisk: number;    // 性能风险 (0-10)
  compatibilityRisk: number;  // 兼容性风险 (0-10)
}

export interface ComplexityAnalysis {
  score: number;              // 总体复杂度分数 (1-100)
  level: 'trivial' | 'simple' | 'medium' | 'complex' | 'very-complex' | 'extreme';
  factors: ComplexityFactors;
  confidence: number;         // 评估置信度 (0-1)
  estimatedHours: number;     // 预估工时
  recommendedApproach: string;
  risks: string[];
  mitigations: string[];
  breakdown?: TaskBreakdown;
}

export interface TaskBreakdown {
  shouldSplit: boolean;
  reason: string;
  suggestedSubtasks: Array<{
    title: string;
    estimatedHours: number;
    complexity: number;
    dependencies: string[];
  }>;
}

export class ComplexityAnalyzer {
  // 技术关键词及其权重
  private technicalKeywords = {
    architecture: 3,
    framework: 2,
    integration: 3,
    migration: 3,
    refactor: 3,
    optimization: 2,
    security: 3,
    authentication: 2,
    authorization: 2,
    encryption: 3,
    performance: 2,
    scalability: 3,
    distributed: 3,
    microservices: 3,
    kubernetes: 3,
    docker: 2,
    database: 2,
    cache: 2,
    queue: 2,
    realtime: 3,
    websocket: 2,
    graphql: 2,
    api: 1,
    testing: 1,
    deployment: 2,
    ci: 2,
    cd: 2,
    monitoring: 2,
    logging: 1,
    analytics: 2,
    machine_learning: 4,
    ai: 3,
    algorithm: 2,
    data_structure: 2,
    concurrent: 3,
    parallel: 3,
    async: 2,
    blockchain: 4,
    crypto: 3
  };

  // 不确定性标记
  private uncertaintyMarkers = [
    'maybe', 'possibly', 'might', 'could', 'should', 'evaluate',
    'research', 'investigate', 'explore', 'consider', 'probably',
    '可能', '也许', '或许', '考虑', '研究', '探索', '评估'
  ];

  // 集成标记
  private integrationMarkers = [
    'integrate', 'connect', 'sync', 'api', 'webhook', 'third-party',
    'external', 'service', 'platform', '集成', '连接', '同步', '第三方'
  ];

  /**
   * 分析任务复杂度
   */
  async analyzeComplexity(task: string | Task, context?: any): Promise<ComplexityAnalysis> {
    const taskContent = typeof task === 'string' ? task : task.content;
    const priority = typeof task === 'string' ? 'medium' : task.priority;
    
    logger.info(`Analyzing complexity for: ${taskContent.substring(0, 50)}...`);
    
    // 计算各项因素
    const factors = this.calculateFactors(taskContent, priority, context);
    
    // 计算总体分数
    const score = this.calculateScore(factors);
    
    // 确定复杂度级别
    const level = this.determineLevel(score);
    
    // 估算工时
    const estimatedHours = this.estimateHours(score, level, priority);
    
    // 生成建议
    const { recommendedApproach, risks, mitigations } = this.generateRecommendations(
      factors, 
      level, 
      taskContent
    );
    
    // 判断是否需要拆分
    const breakdown = this.analyzeBreakdown(taskContent, score, estimatedHours);
    
    // 计算置信度
    const confidence = this.calculateConfidence(taskContent, factors);
    
    return {
      score,
      level,
      factors,
      confidence,
      estimatedHours,
      recommendedApproach,
      risks,
      mitigations,
      breakdown
    };
  }

  /**
   * 计算复杂度因素
   */
  private calculateFactors(content: string, priority: TaskPriority, context?: any): ComplexityFactors {
    const lowerContent = content.toLowerCase();
    
    return {
      // 基础因素
      textLength: this.calculateTextComplexity(content),
      technicalTerms: this.calculateTechnicalDensity(lowerContent),
      dependencies: this.calculateDependencyComplexity(content, context),
      uncertainty: this.calculateUncertainty(lowerContent),
      
      // 技术因素
      integration: this.calculateIntegrationComplexity(lowerContent),
      algorithmicDepth: this.calculateAlgorithmicDepth(lowerContent),
      dataComplexity: this.calculateDataComplexity(lowerContent),
      
      // 范围因素
      scope: this.calculateScope(content, priority),
      crossFunctional: this.calculateCrossFunctional(lowerContent),
      userImpact: this.calculateUserImpact(lowerContent, priority),
      
      // 风险因素
      securityRisk: this.calculateSecurityRisk(lowerContent),
      performanceRisk: this.calculatePerformanceRisk(lowerContent),
      compatibilityRisk: this.calculateCompatibilityRisk(lowerContent)
    };
  }

  /**
   * 计算文本复杂度
   */
  private calculateTextComplexity(content: string): number {
    const length = content.length;
    if (length < 50) return 2;
    if (length < 100) return 3;
    if (length < 200) return 5;
    if (length < 500) return 7;
    return 8;
  }

  /**
   * 计算技术术语密度
   */
  private calculateTechnicalDensity(content: string): number {
    const words = content.split(/\s+/);
    let technicalScore = 0;
    let technicalCount = 0;
    
    for (const word of words) {
      for (const [keyword, weight] of Object.entries(this.technicalKeywords)) {
        if (word.includes(keyword)) {
          technicalScore += weight;
          technicalCount++;
        }
      }
    }
    
    const density = technicalCount / words.length;
    const weightedScore = technicalScore / words.length;
    
    return Math.min(10, Math.round(density * 20 + weightedScore * 5));
  }

  /**
   * 计算依赖复杂度
   */
  private calculateDependencyComplexity(content: string, context?: any): number {
    let score = 3; // 基础分数
    
    // 检查显式依赖
    const dependencyMarkers = ['depends on', 'requires', 'needs', 'after', '依赖', '需要', '之后'];
    for (const marker of dependencyMarkers) {
      if (content.toLowerCase().includes(marker)) {
        score += 2;
      }
    }
    
    // 检查上下文依赖
    if (context?.dependencies?.length > 0) {
      score += Math.min(3, context.dependencies.length);
    }
    
    // 检查外部系统依赖
    if (content.match(/api|service|database|third.?party/i)) {
      score += 2;
    }
    
    return Math.min(10, score);
  }

  /**
   * 计算不确定性
   */
  private calculateUncertainty(content: string): number {
    let score = 0;
    
    for (const marker of this.uncertaintyMarkers) {
      if (content.includes(marker)) {
        score += 2;
      }
    }
    
    // 检查问号
    const questionMarks = (content.match(/\?/g) || []).length;
    score += questionMarks;
    
    return Math.min(10, score);
  }

  /**
   * 计算集成复杂度
   */
  private calculateIntegrationComplexity(content: string): number {
    let score = 0;
    
    for (const marker of this.integrationMarkers) {
      if (content.includes(marker)) {
        score += 2;
      }
    }
    
    // 特定集成模式
    if (content.match(/oauth|saml|ldap|sso/i)) score += 3;
    if (content.match(/payment|stripe|paypal/i)) score += 3;
    if (content.match(/email|sms|notification/i)) score += 2;
    if (content.match(/import|export|migration/i)) score += 2;
    
    return Math.min(10, score);
  }

  /**
   * 计算算法深度
   */
  private calculateAlgorithmicDepth(content: string): number {
    let score = 2; // 基础分数
    
    const algorithmicTerms = {
      'algorithm': 3,
      'optimize': 2,
      'performance': 2,
      'complexity': 2,
      'recursive': 3,
      'dynamic programming': 4,
      'machine learning': 4,
      'neural network': 5,
      'data structure': 2,
      'graph': 3,
      'tree': 2,
      'search': 2,
      'sort': 2,
      'cache': 2,
      'index': 2
    };
    
    for (const [term, weight] of Object.entries(algorithmicTerms)) {
      if (content.includes(term)) {
        score += weight;
      }
    }
    
    return Math.min(10, score);
  }

  /**
   * 计算数据复杂度
   */
  private calculateDataComplexity(content: string): number {
    let score = 2;
    
    // 数据操作
    if (content.match(/crud|create|read|update|delete/i)) score += 1;
    if (content.match(/batch|bulk|mass/i)) score += 2;
    if (content.match(/transaction|atomic/i)) score += 2;
    if (content.match(/migration|transform/i)) score += 3;
    
    // 数据规模
    if (content.match(/million|billion|large.?scale/i)) score += 3;
    if (content.match(/real.?time|streaming/i)) score += 2;
    
    // 数据类型
    if (content.match(/json|xml|csv|binary/i)) score += 1;
    if (content.match(/encrypt|decrypt|hash/i)) score += 2;
    
    return Math.min(10, score);
  }

  /**
   * 计算影响范围
   */
  private calculateScope(content: string, priority: TaskPriority): number {
    let score = priority === 'high' ? 5 : priority === 'low' ? 3 : 4;
    
    // 范围标记
    if (content.match(/entire|whole|all|global|system.?wide/i)) score += 3;
    if (content.match(/module|component|feature/i)) score += 1;
    if (content.match(/critical|core|essential/i)) score += 2;
    
    return Math.min(10, score);
  }

  /**
   * 计算跨功能程度
   */
  private calculateCrossFunctional(content: string): number {
    let score = 2;
    const areas = ['frontend', 'backend', 'database', 'devops', 'mobile', 'desktop', 'api', 'ui', 'ux'];
    let mentionedAreas = 0;
    
    for (const area of areas) {
      if (content.includes(area)) {
        mentionedAreas++;
      }
    }
    
    score += mentionedAreas * 2;
    
    return Math.min(10, score);
  }

  /**
   * 计算用户影响
   */
  private calculateUserImpact(content: string, priority: TaskPriority): number {
    let score = priority === 'high' ? 5 : 3;
    
    if (content.match(/user|customer|client/i)) score += 2;
    if (content.match(/ui|ux|interface|experience/i)) score += 2;
    if (content.match(/breaking.?change|migration/i)) score += 3;
    if (content.match(/downtime|maintenance/i)) score += 2;
    
    return Math.min(10, score);
  }

  /**
   * 计算安全风险
   */
  private calculateSecurityRisk(content: string): number {
    let score = 0;
    
    const securityTerms = {
      'authentication': 3,
      'authorization': 3,
      'security': 3,
      'encryption': 4,
      'password': 3,
      'token': 2,
      'oauth': 3,
      'vulnerability': 4,
      'injection': 5,
      'xss': 4,
      'csrf': 4,
      'permission': 2,
      'access control': 3
    };
    
    for (const [term, weight] of Object.entries(securityTerms)) {
      if (content.toLowerCase().includes(term)) {
        score = Math.max(score, weight);
      }
    }
    
    return score;
  }

  /**
   * 计算性能风险
   */
  private calculatePerformanceRisk(content: string): number {
    let score = 0;
    
    if (content.match(/performance|optimize|speed/i)) score += 3;
    if (content.match(/scale|scaling|scalability/i)) score += 3;
    if (content.match(/real.?time|low.?latency/i)) score += 4;
    if (content.match(/cache|caching|index/i)) score += 2;
    if (content.match(/million|billion/i)) score += 3;
    
    return Math.min(10, score);
  }

  /**
   * 计算兼容性风险
   */
  private calculateCompatibilityRisk(content: string): number {
    let score = 0;
    
    if (content.match(/compatibility|compatible/i)) score += 3;
    if (content.match(/legacy|old|deprecated/i)) score += 3;
    if (content.match(/migration|upgrade/i)) score += 4;
    if (content.match(/version|versioning/i)) score += 2;
    if (content.match(/browser|device|platform/i)) score += 2;
    
    return Math.min(10, score);
  }

  /**
   * 计算总体复杂度分数
   */
  private calculateScore(factors: ComplexityFactors): number {
    // 权重配置
    const weights = {
      // 基础因素 (30%)
      textLength: 0.05,
      technicalTerms: 0.10,
      dependencies: 0.10,
      uncertainty: 0.05,
      
      // 技术因素 (35%)
      integration: 0.15,
      algorithmicDepth: 0.10,
      dataComplexity: 0.10,
      
      // 范围因素 (20%)
      scope: 0.10,
      crossFunctional: 0.05,
      userImpact: 0.05,
      
      // 风险因素 (15%)
      securityRisk: 0.05,
      performanceRisk: 0.05,
      compatibilityRisk: 0.05
    };
    
    let weightedSum = 0;
    for (const [factor, value] of Object.entries(factors)) {
      weightedSum += value * weights[factor as keyof ComplexityFactors];
    }
    
    // 转换为 1-100 分数
    return Math.round(weightedSum * 10);
  }

  /**
   * 确定复杂度级别
   */
  private determineLevel(score: number): ComplexityAnalysis['level'] {
    if (score <= 15) return 'trivial';
    if (score <= 30) return 'simple';
    if (score <= 50) return 'medium';
    if (score <= 70) return 'complex';
    if (score <= 85) return 'very-complex';
    return 'extreme';
  }

  /**
   * 估算工时
   */
  private estimateHours(score: number, level: ComplexityAnalysis['level'], priority: TaskPriority): number {
    // 基础工时映射
    const baseHours = {
      'trivial': 0.5,
      'simple': 2,
      'medium': 4,
      'complex': 8,
      'very-complex': 16,
      'extreme': 32
    };
    
    let hours = baseHours[level];
    
    // 根据具体分数微调
    const levelRange = 15; // 每个级别的分数范围
    const scoreInLevel = score % levelRange;
    const adjustment = (scoreInLevel / levelRange) * (hours * 0.5);
    hours += adjustment;
    
    // 优先级调整
    if (priority === 'high') {
      hours *= 0.9; // 高优先级可能需要加急，但也可能因此质量下降
    } else if (priority === 'low') {
      hours *= 1.1; // 低优先级可能拖延
    }
    
    return Math.round(hours * 10) / 10; // 保留一位小数
  }

  /**
   * 生成建议
   */
  private generateRecommendations(
    factors: ComplexityFactors, 
    level: ComplexityAnalysis['level'],
    content: string
  ): {
    recommendedApproach: string;
    risks: string[];
    mitigations: string[];
  } {
    const risks: string[] = [];
    const mitigations: string[] = [];
    let recommendedApproach = '';
    
    // 基于级别的通用建议
    switch (level) {
      case 'trivial':
        recommendedApproach = '直接实现，无需特殊规划';
        break;
      case 'simple':
        recommendedApproach = '标准开发流程，注意代码质量';
        break;
      case 'medium':
        recommendedApproach = '先设计后实现，编写单元测试';
        break;
      case 'complex':
        recommendedApproach = '详细设计文档，分阶段实现，充分测试';
        break;
      case 'very-complex':
        recommendedApproach = '技术评审，原型验证，增量开发，持续集成';
        break;
      case 'extreme':
        recommendedApproach = '专家评审，概念验证，风险评估，敏捷迭代';
        break;
    }
    
    // 基于具体因素的风险和缓解措施
    if (factors.uncertainty > 7) {
      risks.push('需求不明确，可能导致返工');
      mitigations.push('与利益相关者充分沟通，明确需求');
      mitigations.push('采用原型或 MVP 方式验证');
    }
    
    if (factors.integration > 7) {
      risks.push('第三方依赖可能不稳定');
      mitigations.push('评估备选方案');
      mitigations.push('实现适配器模式便于切换');
      mitigations.push('准备降级方案');
    }
    
    if (factors.securityRisk > 6) {
      risks.push('安全漏洞风险');
      mitigations.push('进行安全审查');
      mitigations.push('使用成熟的安全框架');
      mitigations.push('实施安全测试');
    }
    
    if (factors.performanceRisk > 6) {
      risks.push('性能瓶颈风险');
      mitigations.push('进行性能基准测试');
      mitigations.push('设计可扩展架构');
      mitigations.push('准备性能优化方案');
    }
    
    if (factors.dependencies > 7) {
      risks.push('依赖关系复杂，可能产生阻塞');
      mitigations.push('明确依赖关系图');
      mitigations.push('并行开发独立模块');
      mitigations.push('定义清晰的接口契约');
    }
    
    return { recommendedApproach, risks, mitigations };
  }

  /**
   * 分析是否需要拆分任务
   */
  private analyzeBreakdown(content: string, score: number, estimatedHours: number): TaskBreakdown | undefined {
    // 如果预估时间超过 8 小时或复杂度超过 70，建议拆分
    if (estimatedHours <= 8 && score <= 70) {
      return undefined;
    }
    
    const shouldSplit = true;
    const reason = estimatedHours > 8 
      ? `预估时间 ${estimatedHours} 小时，建议拆分为更小的任务`
      : `复杂度评分 ${score}/100，建议分解降低风险`;
    
    // 生成子任务建议
    const suggestedSubtasks = this.generateSubtasks(content, score, estimatedHours);
    
    return {
      shouldSplit,
      reason,
      suggestedSubtasks
    };
  }

  /**
   * 生成子任务建议
   */
  private generateSubtasks(
    content: string, 
    score: number, 
    totalHours: number
  ): TaskBreakdown['suggestedSubtasks'] {
    const subtasks: TaskBreakdown['suggestedSubtasks'] = [];
    const lowerContent = content.toLowerCase();
    
    // 通用任务分解模式
    if (lowerContent.includes('crud') || lowerContent.match(/create.*read.*update.*delete/)) {
      subtasks.push(
        { title: '设计数据模型和 API 接口', estimatedHours: totalHours * 0.2, complexity: 30, dependencies: [] },
        { title: '实现创建(Create)功能', estimatedHours: totalHours * 0.2, complexity: 40, dependencies: ['设计数据模型和 API 接口'] },
        { title: '实现查询(Read)功能', estimatedHours: totalHours * 0.15, complexity: 30, dependencies: ['设计数据模型和 API 接口'] },
        { title: '实现更新(Update)功能', estimatedHours: totalHours * 0.2, complexity: 40, dependencies: ['设计数据模型和 API 接口'] },
        { title: '实现删除(Delete)功能', estimatedHours: totalHours * 0.15, complexity: 30, dependencies: ['设计数据模型和 API 接口'] },
        { title: '编写单元测试和集成测试', estimatedHours: totalHours * 0.1, complexity: 30, dependencies: ['实现创建(Create)功能', '实现查询(Read)功能'] }
      );
    } else if (lowerContent.includes('authentication') || lowerContent.includes('认证')) {
      subtasks.push(
        { title: '设计认证架构和流程', estimatedHours: totalHours * 0.2, complexity: 50, dependencies: [] },
        { title: '实现用户注册功能', estimatedHours: totalHours * 0.15, complexity: 40, dependencies: ['设计认证架构和流程'] },
        { title: '实现登录和 Token 生成', estimatedHours: totalHours * 0.2, complexity: 50, dependencies: ['设计认证架构和流程'] },
        { title: '实现 Token 验证中间件', estimatedHours: totalHours * 0.15, complexity: 40, dependencies: ['实现登录和 Token 生成'] },
        { title: '实现登出和 Token 撤销', estimatedHours: totalHours * 0.1, complexity: 30, dependencies: ['实现登录和 Token 生成'] },
        { title: '添加安全措施（防暴力破解等）', estimatedHours: totalHours * 0.1, complexity: 40, dependencies: ['实现登录和 Token 生成'] },
        { title: '编写认证测试用例', estimatedHours: totalHours * 0.1, complexity: 30, dependencies: ['实现 Token 验证中间件'] }
      );
    } else if (lowerContent.match(/integrat|集成|connect|连接/)) {
      subtasks.push(
        { title: '研究第三方服务 API 文档', estimatedHours: totalHours * 0.15, complexity: 20, dependencies: [] },
        { title: '设计集成架构和数据流', estimatedHours: totalHours * 0.2, complexity: 50, dependencies: ['研究第三方服务 API 文档'] },
        { title: '实现 API 客户端封装', estimatedHours: totalHours * 0.25, complexity: 40, dependencies: ['设计集成架构和数据流'] },
        { title: '实现数据同步逻辑', estimatedHours: totalHours * 0.2, complexity: 50, dependencies: ['实现 API 客户端封装'] },
        { title: '添加错误处理和重试机制', estimatedHours: totalHours * 0.1, complexity: 40, dependencies: ['实现数据同步逻辑'] },
        { title: '测试集成功能', estimatedHours: totalHours * 0.1, complexity: 30, dependencies: ['添加错误处理和重试机制'] }
      );
    } else {
      // 通用拆分策略
      const phases = ['设计', '核心实现', '测试', '文档'];
      const phaseHours = [0.25, 0.45, 0.2, 0.1];
      const phaseComplexity = [40, 60, 30, 20];
      
      phases.forEach((phase, index) => {
        subtasks.push({
          title: `${phase}阶段`,
          estimatedHours: totalHours * phaseHours[index],
          complexity: Math.min(100, score * phaseComplexity[index] / 100),
          dependencies: index > 0 ? [`${phases[0]}阶段`] : []
        });
      });
    }
    
    return subtasks;
  }

  /**
   * 计算评估置信度
   */
  private calculateConfidence(content: string, factors: ComplexityFactors): number {
    let confidence = 0.7; // 基础置信度
    
    // 内容长度影响置信度
    if (content.length > 200) confidence += 0.1;
    if (content.length > 500) confidence += 0.1;
    
    // 不确定性降低置信度
    confidence -= factors.uncertainty * 0.02;
    
    // 技术术语提高置信度（说明描述具体）
    confidence += Math.min(0.1, factors.technicalTerms * 0.02);
    
    return Math.max(0.1, Math.min(1, confidence));
  }

  /**
   * 批量分析任务复杂度
   */
  async batchAnalyze(tasks: Array<string | Task>): Promise<ComplexityAnalysis[]> {
    const results: ComplexityAnalysis[] = [];
    
    for (const task of tasks) {
      const analysis = await this.analyzeComplexity(task);
      results.push(analysis);
    }
    
    return results;
  }

  /**
   * 获取复杂度报告
   */
  generateReport(analyses: ComplexityAnalysis[]): string {
    const total = analyses.length;
    const avgScore = analyses.reduce((sum, a) => sum + a.score, 0) / total;
    const totalHours = analyses.reduce((sum, a) => sum + a.estimatedHours, 0);
    
    const distribution = {
      trivial: analyses.filter(a => a.level === 'trivial').length,
      simple: analyses.filter(a => a.level === 'simple').length,
      medium: analyses.filter(a => a.level === 'medium').length,
      complex: analyses.filter(a => a.level === 'complex').length,
      'very-complex': analyses.filter(a => a.level === 'very-complex').length,
      extreme: analyses.filter(a => a.level === 'extreme').length
    };
    
    const needsSplitting = analyses.filter(a => a.breakdown?.shouldSplit).length;
    const highRisk = analyses.filter(a => 
      a.factors.securityRisk > 6 || 
      a.factors.performanceRisk > 6 || 
      a.factors.compatibilityRisk > 6
    ).length;
    
    return `
# 任务复杂度分析报告

## 总体统计
- 任务总数: ${total}
- 平均复杂度: ${avgScore.toFixed(1)}/100
- 预估总工时: ${totalHours.toFixed(1)} 小时
- 需要拆分的任务: ${needsSplitting}
- 高风险任务: ${highRisk}

## 复杂度分布
- 琐碎 (Trivial): ${distribution.trivial} (${(distribution.trivial/total*100).toFixed(1)}%)
- 简单 (Simple): ${distribution.simple} (${(distribution.simple/total*100).toFixed(1)}%)
- 中等 (Medium): ${distribution.medium} (${(distribution.medium/total*100).toFixed(1)}%)
- 复杂 (Complex): ${distribution.complex} (${(distribution.complex/total*100).toFixed(1)}%)
- 非常复杂 (Very Complex): ${distribution['very-complex']} (${(distribution['very-complex']/total*100).toFixed(1)}%)
- 极度复杂 (Extreme): ${distribution.extreme} (${(distribution.extreme/total*100).toFixed(1)}%)

## 建议
${this.generateReportRecommendations(analyses)}
    `.trim();
  }

  /**
   * 生成报告建议
   */
  private generateReportRecommendations(analyses: ComplexityAnalysis[]): string {
    const recommendations: string[] = [];
    
    const avgScore = analyses.reduce((sum, a) => sum + a.score, 0) / analyses.length;
    if (avgScore > 60) {
      recommendations.push('- 整体复杂度偏高，建议增加技术评审和设计阶段');
    }
    
    const extremeTasks = analyses.filter(a => a.level === 'extreme').length;
    if (extremeTasks > 0) {
      recommendations.push(`- 发现 ${extremeTasks} 个极度复杂任务，建议专家介入或重新评估可行性`);
    }
    
    const needsSplitting = analyses.filter(a => a.breakdown?.shouldSplit).length;
    if (needsSplitting > analyses.length * 0.3) {
      recommendations.push('- 超过 30% 的任务需要拆分，建议重新审视任务粒度');
    }
    
    const highUncertainty = analyses.filter(a => a.factors.uncertainty > 7).length;
    if (highUncertainty > 0) {
      recommendations.push(`- ${highUncertainty} 个任务存在高不确定性，需要进一步澄清需求`);
    }
    
    return recommendations.join('\n') || '- 任务复杂度分布合理，可以按计划执行';
  }
}